/**
 * Registering the service worker, and what to do when a new one is ready.
 *
 * <p>The worker itself is public/sw.js — plain JavaScript, copied as it is, because it runs
 * outside the bundle.</p>
 */

/** Called when a new version has installed and is waiting for permission to take over. */
export type UpdateReady = (accept: () => void) => void;

/**
 * Registers the worker, if this browser and this page can have one.
 *
 * <p>Production only. In development Vite serves modules it expects to replace as you save,
 * and a worker caching them turns every edit into "why is it still showing the old one" —
 * which costs more hours than it saves.</p>
 *
 * <p>Needs a secure context, which in practice means HTTPS or localhost. A phone opening
 * <code>http://10.0.0.x:5174</code> over the office network has neither, so nothing is
 * registered and the app works exactly as it did before — online only. That is also why the
 * field test needs the tunnel rather than a LAN address.</p>
 */
export function registerServiceWorker(onUpdateReady?: UpdateReady): void {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;

  // After load, not during it. Registering earlier makes the worker's own install compete
  // for the connection with the page that is still fetching its bundle.
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        // Already waiting: a new version installed on an earlier visit and the person never
        // took it.
        if (registration.waiting) offer(registration, onUpdateReady);

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.addEventListener('statechange', () => {
            // Installed with a controller already in place means this is a replacement
            // rather than the first install — the only case worth telling somebody about.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              offer(registration, onUpdateReady);
            }
          });
        });
      })
      .catch(() => {
        // A failed registration is not a failed app. It goes back to behaving as it did
        // before there was a worker, which is to say it needs a signal.
      });
  });
}

function offer(registration: ServiceWorkerRegistration, onUpdateReady?: UpdateReady): void {
  if (!onUpdateReady) return;

  onUpdateReady(() => {
    registration.waiting?.postMessage('skip-waiting');

    // Reloaded once the new worker has taken over, not immediately: reloading first would
    // fetch the shell from the old one and show the same version again.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    });
  });
}
